import { TextInputProps } from "@applyboard/crystal-ui"
import { ConfidenceLevel, Passport } from "../../../../hooks/useGetPassport"
import { ExtractionFields, PersonalInformationFormFields } from "./types"
import { convertTimelessDateStrToLocalDate } from "../../../../utils"
import { StudentApplication } from "../../types"
import { isEqual } from "lodash"
import { Student } from "../../../../hooks/useGetStudent"

export function copyResidentialAddress({
  residentialAddressCountry,
  residentialAddressLine1,
  residentialAddressLine2,
  residentialAddressCity,
  residentialAddressProvince,
  residentialAddressPostalCode,
}: {
  residentialAddressCountry: string
  residentialAddressLine1: string
  residentialAddressLine2: string
  residentialAddressCity: string
  residentialAddressProvince: string
  residentialAddressPostalCode: string
}) {
  return {
    mailingAddressCountry: residentialAddressCountry,
    mailingAddressLine1: residentialAddressLine1,
    mailingAddressLine2: residentialAddressLine2,
    mailingAddressCity: residentialAddressCity,
    mailingAddressProvince: residentialAddressProvince,
    mailingAddressPostalCode: residentialAddressPostalCode,
  }
}

export function hasUserModifiedExtractedPassportDetails(
  data: PersonalInformationFormFields,
  passport?: Passport,
) {
  const passportNumberModified =
    data.passportNumber !== null && data.passportNumber !== passport?.attributes.passportNumber
  const passportExpiryModified =
    data.passportExpiryDate !== null && data.passportExpiryDate !== passport?.attributes.expiryDate

  return passportNumberModified || passportExpiryModified
}

type areAddressesEqualParams = {
  application: StudentApplication
}

function areAddressesEqual(params: areAddressesEqualParams) {
  if (
    !params.application?.attributes?.personalInformation?.addresses ||
    isEqual(
      params.application.attributes?.personalInformation?.addresses?.residentialAddress,
      params.application.attributes?.personalInformation?.addresses?.mailingAddress,
    )
  ) {
    return true
  }
  return false
}


type DefaultValuesProps = {
  passport?: Passport
  application: StudentApplication
  hasLegacyDocumentsSchema: boolean
  student?: Student
}

export function getDefaultValue({ passport, application, hasLegacyDocumentsSchema, student }: DefaultValuesProps) {
  const { basicPersonalInformation } = application?.attributes?.personalInformation || {}
  const { residentialAddress, mailingAddress } =
    application?.attributes?.personalInformation?.addresses || {}
  const { extractedData } = passport?.attributes.processingResults || {}

  function getFieldValue(applicationValue?: string, passportValue?: string) {
    if (Object.values(application.attributes?.statusAndCitizenship?.passports || {})[0]?.passportId || hasLegacyDocumentsSchema) {
      return applicationValue && applicationValue !== 'null' ? applicationValue : ''
    }

    return passportValue && passportValue !== 'null' ? passportValue : ''
  }

  function getDateFieldValue(applicationValue?: string, passportValue?: string) {
    const fieldValue = getFieldValue(applicationValue, passportValue)

    if (fieldValue !== '' && new Date(fieldValue).toString() !== 'Invalid Date') {
      return convertTimelessDateStrToLocalDate(fieldValue).toISOString()
    }

    return ''
  }

  return {
    passportNumber: getFieldValue(
      hasLegacyDocumentsSchema
        ? Object.values(application?.attributes?.statusAndCitizenship?.passports || {})[0]?.passportNumber
        : passport?.attributes.passportNumber,
      extractedData?.passportNumber?.value,
    ),
    passportExpiryDate: getDateFieldValue(
      hasLegacyDocumentsSchema
        ? Object.values(application?.attributes?.statusAndCitizenship?.passports || {})[0]?.passportExpiry
        : passport?.attributes.expiryDate,
      extractedData?.expiryDate?.value,
    ),
    passportFiles: [],
    immigrationStatus:
      application?.attributes?.statusAndCitizenship?.destinationCountryStatus || '',
    immigrationFiles: [],
    givenName: getFieldValue(basicPersonalInformation?.givenName, extractedData?.firstName?.value),
    middleName: getFieldValue(
      basicPersonalInformation?.middleName,
      extractedData?.middleName?.value,
    ),
    familyName: getFieldValue(
      basicPersonalInformation?.familyName,
      extractedData?.familyName?.value,
    ),
    previousName: student?.attributes?.personalInformation?.previousName ?? '',
    preferredName: student?.attributes?.personalInformation?.preferredName ?? '',
    dateOfBirth: getDateFieldValue(
      basicPersonalInformation?.dateOfBirth,
      extractedData?.birthDate?.value,
    ),
    nationality: getFieldValue(
      basicPersonalInformation?.nationality,
      extractedData?.nationality?.value,
    ),
    firstLanguageSpoken: student?.attributes?.personalInformation?.firstLanguageSpoken ?? '',
    gender: getFieldValue(basicPersonalInformation?.gender, extractedData?.sex?.value),
    residentialAddressCountry: getFieldValue(
      residentialAddress?.country,
      extractedData?.address?.country,
    ),
    residentialAddressLine1: getFieldValue(
      residentialAddress?.addressLine1,
      extractedData?.address?.address,
    ),
    residentialAddressLine2: residentialAddress?.addressLine2 || '',
    residentialAddressCity: getFieldValue(residentialAddress?.city, extractedData?.address?.city),
    residentialAddressProvince: getFieldValue(
      residentialAddress?.province,
      extractedData?.address?.province,
    ),
    residentialAddressPostalCode: getFieldValue(
      residentialAddress?.postalCode,
      extractedData?.address?.postalCode,
    ),
    isMailingAddressResidentialAddress: areAddressesEqual({
      application: application,
    }),
    mailingAddressCountry: mailingAddress?.country || '',
    mailingAddressLine1: mailingAddress?.addressLine1 || '',
    mailingAddressLine2: mailingAddress?.addressLine2 || '',
    mailingAddressCity: mailingAddress?.city || '',
    mailingAddressProvince: mailingAddress?.province || '',
    mailingAddressPostalCode: mailingAddress?.postalCode || '',
  }
}

type ExtractionHelperTextProps = {
  applicationPassportId?: string
  extractedData?: {
    value?: string
    confidence?: ConfidenceLevel
  }
  passportId?: string
  field?: ExtractionFields
  currentValue?: string
}

type ExtractionHelperTextType = {
  helpText?: string
  intent?: TextInputProps['intent']
}

export function getExtractionHelperText({
  applicationPassportId,
  extractedData,
  passportId,
  field,
  currentValue,
}: ExtractionHelperTextProps): ExtractionHelperTextType {
  if (
    !passportId ||
    applicationPassportId === passportId ||
    (currentValue && currentValue !== extractedData?.value)
  ) {
    return {}
  }

  let intent: TextInputProps['intent'] = 'secondary'
  let helpText = ''

  switch (extractedData?.confidence) {
    case ConfidenceLevel.LOW:
      intent = 'warning'
      helpText = 'This data may be incorrect. Please review.'
      break
    case ConfidenceLevel.NONE:
      intent = 'negative'
      helpText = 'This data wasn’t extracted. Please provide manual input.'
      break
    case ConfidenceLevel.HIGH:
      if (field === 'firstName') {
        helpText = 'Given name as displayed on passport'
      } else if (field === 'familyName') {
        helpText = 'Family name as displayed on passport'
      }
      break
  }

  return {
    helpText,
    intent,
  }
}
